<template>
  <BaseModal
    v-if="$page.props.shared.recordableCallsModal"
    :name="RECORDING_FEATURE_PRESENTATION_MODAL_NAME"
    :click-outside-to-close="false"
    width-class="max-w-[500px] overflow-hidden"
    modal-class="pt-32"
    is-background-blurred
    :auto-focus="false"
  >
    <div class="video-room-modal-background px-4 pt-10 pb-4">
      <BaseCarousel
        :slides-data="slidesData"
        is-pagination-enabled
        arrow-classes="bg-neutral-900 text-white border-none h-8 w-8"
        arrow-icon-classes="w-3 h-4"
        :breakpoints="{
          1: { slidesPerView: 1 },
        }"
      >
        <template #slide="{ slideData }">
          <div class="text-center">
            <div class="display-1">{{ slideData.title }}</div>
            <div class="mt-2 text-sm text-neutral-600">
              {{ slideData.description }}
            </div>
            <img
              v-if="slideData.id === 1"
              src="@/assets/images/video-room-chat-gpt.png"
              alt=""
              class="mx-auto mt-6 w-64"
            />
            <img :src="slideData.image" alt="" class="mt-6 w-full" />
          </div>
          <div class="p-4">
            <p class="text-sm text-neutral-600">
              {{ slideData.bottomDescription }}
            </p>
            <!-- prettier-ignore -->
            <p class="mt-4 text-neutral-600 text-sm">
              Want to opt out? You can disable recordings in <button @click="navigateToSettings" class="link">Settings</button>.
            </p>
          </div>
        </template>
      </BaseCarousel>
      <div>
        <div class="text-center text-xs text-neutral-400 underline">
          <a
            href="/faq/ai-recording"
            target="_blank"
          >
            Want to learn more about how AI Recordings work?
          </a>
        </div>
        <div class="mt-6 flex justify-center">
          <BaseButton
            @click="closeModal(RECORDING_FEATURE_PRESENTATION_MODAL_NAME)"
            color="outline-gray"
            size="large"
          >
            Close
          </BaseButton>
        </div>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { RECORDING_FEATURE_PRESENTATION_MODAL_NAME } from '@/shared/data/globalVariables.js'
import BaseModal from '@/shared/components/BaseModal.vue'
import BaseCarousel from '@/shared/components/BaseCarousel.vue'
import FirstCardImage from '@/assets/images/video-recording-card-1.png'
import SecondCardImage from '@/assets/images/video-recording-card-2.png'
import ThirdCardImage from '@/assets/images/video-recording-card-3.png'
import BaseButton from '@/shared/components/BaseButton.vue'
import { useModals } from '@/shared/composables/useModals.js'
import { router } from '@inertiajs/vue3'

const { closeModal } = useModals()

const slidesData = [
  {
    id: 1,
    title: 'Your Mentor Sessions Just Got Smarter!',
    description:
      'Every session now comes with AI-powered summaries and actionable insights to help you get more value from your conversations.',
    image: FirstCardImage,
    bottomDescription:
      "We've upgraded our Video Room with automatic recording and AI analysis. Get structured summaries, key takeaways, and follow-up actions from every session - all in one place.",
  },
  {
    id: 2,
    title: 'Your Personal Growth Library',
    description:
      'All your recorded sessions, organized and analyzed for easy reference.',
    image: SecondCardImage,
    bottomDescription:
      'Browse through your past sessions, complete with AI summaries and insights. Never lose track of valuable advice and action items from your mentoring conversations.',
  },
  {
    id: 3,
    title: 'Smart Session Insights',
    description: 'Get more from every conversation with AI-powered analysis',
    image: ThirdCardImage,
    bottomDescription:
      'Your AI summary transforms each session into a structured overview with discussion highlights, concrete next steps, and targeted follow-up questions. Review key points anytime to stay on track with your growth goals.',
  },
]

const navigateToSettings = () => {
  closeModal(RECORDING_FEATURE_PRESENTATION_MODAL_NAME)
  router.visit('/settings/profile/communication')
}
</script>

<style scoped>
@reference "#tailwind-theme";

.video-room-modal-background {
  background-image: url('@/assets/images/video-room-modal-background.png');
  @apply bg-cover bg-center bg-no-repeat;
}
</style>
